// app/guides/by-budget.tsx — בחירת קורס לפי תקציב | Kursi
import Head from 'expo-router/head';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
} from 'react-native';
import { useRouter, Link } from 'expo-router';
import { theme } from '@/constants/theme';

const SITE_URL = 'https://kursiaii.com';

const breadcrumbSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'קורסים', item: `${SITE_URL}/courses` },
    { '@type': 'ListItem', position: 3, name: 'בחירת קורס לפי תקציב', item: `${SITE_URL}/guides/by-budget` },
  ],
});

const faqSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'האם קורס יקר יותר = קורס טוב יותר?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'לא בהכרח. מחיר גבוה יכול לשקף מיתוג, מיקום, או שיווק — לא רק איכות תוכן. הדרך לבדוק: לבקש לראות פרויקטים של בוגרים, לקרוא חוות דעת, ולבדוק מי המרצה.',
      },
    },
    {
      '@type': 'Question',
      name: 'כמה עולה קורס תכנות ממוצע בישראל?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'קורס תכנות ממוקד (3–4 חודשים): 3,000–9,000 ₪. בוטקאמפ Full Stack (4–6 חודשים): 14,000–22,000 ₪. קורסים אונליין בינלאומיים: 300–2,000 ₪.',
      },
    },
    {
      '@type': 'Question',
      name: 'האם יש מימון לקורסים מקצועיים?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'כן. קרן הסבה מקצועית, מענקי לשכת התעסוקה, קרנות הכשרה, ומסלולי תשלום מדחוי (ISA) — כאשר משלמים אחרי שמוצאים עבודה. כדאי לשאול את הספק על אפשרויות מימון.',
      },
    },
    {
      '@type': 'Question',
      name: 'איך לוודא שקורס שווה את המחיר?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '3 שאלות מפתח: 1) האם יש פרויקטים של בוגרים שאפשר לראות? 2) האם בוגרים מועסקים בתחום? 3) האם יש מדיניות ביטול/החזר הוגנת?',
      },
    },
  ],
});

const BUDGET_TIERS = [
  {
    label: 'עד 1,500 ₪',
    color: '#DCFCE7',
    borderColor: '#16A34A',
    title: 'מסלול חינמי/בסיסי',
    description: 'קורסים אונליין חינמיים ובמחיר נמוך. מתאים לחקירה ראשונית של תחום.',
    options: [
      'קורסי Coursera, edX — חינמי לצפייה',
      'YouTube — קורסים מלאים בחינם',
      'freeCodeCamp, The Odin Project',
      'קורסי Udemy במבצע (150–400 ₪)',
    ],
    suitable: 'לבדיקת תחום לפני השקעה גדולה',
  },
  {
    label: '1,500 – 6,000 ₪',
    color: '#FEF3C7',
    borderColor: '#D97706',
    title: 'קורסים ממוקדים',
    description: 'קורסים ישראליים ממוקדים, לרוב 2–4 חודשים, נושא ספציפי אחד.',
    options: [
      'קורסי Python בסיסי — 1,500–3,500 ₪',
      'קורסי JavaScript / React — 2,500–5,000 ₪',
      'קורסי שיווק דיגיטלי — 2,000–5,000 ₪',
      'קורסי UI/UX — 2,500–5,500 ₪',
    ],
    suitable: 'לרכישת מיומנות ספציפית אחת',
  },
  {
    label: '6,000 – 15,000 ₪',
    color: '#E0F2FE',
    borderColor: '#0284C7',
    title: 'קורסים מקיפים',
    description: 'קורסים ארוכים יותר (4–6 חודשים), לרוב עם ליווי ופרויקטים.',
    options: [
      'Full Stack בסיסי — 7,000–12,000 ₪',
      'קורס Data Science — 8,000–14,000 ₪',
      'קורס Cyber Security — 8,000–14,000 ₪',
      'קורסי מנטורינג — 6,000–12,000 ₪',
    ],
    suitable: 'לרכישת מיומנות ברת-תעסוקה',
  },
  {
    label: '15,000 ₪+',
    color: '#FAF5EA',
    borderColor: theme.colors.primary,
    title: 'בוטקאמפ מלא',
    description: 'תכנית אינטנסיבית מלאה (4–6 חודשים), 40 שעות בשבוע, מסלול שלם.',
    options: [
      'Full Stack Bootcamp — 15,000–22,000 ₪',
      'Cyber Bootcamp — 15,000–20,000 ₪',
      'Data Science Bootcamp — 14,000–20,000 ₪',
      'מסלולי ISA (תשלום לאחר קבלה לעבודה)',
    ],
    suitable: 'להסבה מקצועית מלאה ומהירה',
  },
];

export default function ByBudgetPage() {
  const router = useRouter();

  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <title>בחירת קורס לפי תקציב 2026 — מה מקבלים בכל טווח מחיר | Kursi</title>
          <meta
            name="description"
            content="מה מקבלים בכל טווח מחיר? מקורסים חינמיים ועד בוטקאמפ מלא — השוואה מלאה של קורסים מקצועיים בישראל לפי תקציב, כולל אפשרויות מימון."
          />
          <link rel="canonical" href={`${SITE_URL}/guides/by-budget`} />
          <meta property="og:url" content={`${SITE_URL}/guides/by-budget`} />
          <meta property="og:title" content="בחירת קורס לפי תקציב 2026 | Kursi" />
          <meta
            property="og:description"
            content="מה מקבלים בכל טווח מחיר? מקורסים חינמיים ועד בוטקאמפ — מדריך לבחירת קורס מקצועי לפי תקציב."
          />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={`${SITE_URL}/og-image.webp`} />
          <meta name="robots" content="index,follow" />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
        </Head>
      )}

      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <View style={styles.navBar}>
          <Link
            href="/courses"
            style={styles.navBtn}
            accessibilityRole="link"
          >
            <Text style={styles.navBtnText}>← קורסים</Text>
          </Link>
          <Link
            href="/"
            style={[styles.navBtn, styles.navBtnHome]}
            accessibilityRole="link"
          >
            <Text style={[styles.navBtnText, styles.navBtnHomeText]}>דף הבית</Text>
          </Link>
        </View>

        <View style={styles.innerContent}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>לפי תקציב</Text>
          </View>

          <Text
            style={styles.h1}
            {...(Platform.OS === 'web' && ({ role: 'heading', 'aria-level': 1 } as any))}
          >
            בחירת קורס לפי תקציב — מה מקבלים בכל טווח מחיר?
          </Text>

          <Text style={styles.intro}>
            תקציב הוא לא הדבר היחיד שחשוב, אבל הוא כן גורם משמעותי. מדריך זה יעזור לכם להבין
            מה ריאלי לצפות בכל טווח מחיר — ואיך לבחור נכון.
          </Text>

          {/* Key insight */}
          <View style={styles.insightCard}>
            <Text style={styles.insightTitle}>עיקרון חשוב: מחיר גבוה ≠ קורס טוב</Text>
            <Text style={styles.insightBody}>
              מחיר גבוה יכול לשקף מיתוג ושיווק, לא רק איכות תוכן. הדרך הנכונה:
              לבדוק פרויקטים של בוגרים, לקרוא חוות דעת אמיתיות, ולשאול את הספק שאלות קשות.
            </Text>
          </View>

          {/* Budget tiers */}
          {BUDGET_TIERS.map((tier, i) => (
            <View key={i} style={[styles.tierCard, { borderColor: tier.borderColor }]}>
              <View style={[styles.tierHeader, { backgroundColor: tier.color }]}>
                <Text style={styles.tierLabel}>{tier.label}</Text>
                <Text style={styles.tierTitle}>{tier.title}</Text>
              </View>
              <View style={styles.tierBody}>
                <Text style={styles.tierDesc}>{tier.description}</Text>
                <View style={styles.bulletList}>
                  {tier.options.map((opt, j) => (
                    <View key={j} style={styles.bulletRow}>
                      <Text style={styles.bulletDot}>•</Text>
                      <Text style={styles.bulletText}>{opt}</Text>
                    </View>
                  ))}
                </View>
                <View style={styles.suitableTag}>
                  <Text style={styles.suitableText}>מתאים ל: {tier.suitable}</Text>
                </View>
              </View>
            </View>
          ))}

          {/* Funding options */}
          <View style={styles.section}>
            <Text style={styles.h2}>אפשרויות מימון לקורסים</Text>
            {[
              { title: 'קרן הסבה מקצועית', desc: 'מממנת קורסים להסבה לתחומים מבוקשים — תכנות, סייבר, דאטה. פנו ללשכת התעסוקה.' },
              { title: 'לשכת התעסוקה', desc: 'מענקים לעובדים מפוטרים ולמחפשי עבודה. עד 80% מימון בתחומים מסוימים.' },
              { title: 'ISA (Income Share Agreement)', desc: 'תשלום רק לאחר שמוצאים עבודה בתחום. קיים בחלק מהבוטקאמפים.' },
              { title: 'תשלומים ללא ריבית', desc: 'רוב הספקים מציעים 12–36 תשלומים ללא ריבית.' },
            ].map((item, i) => (
              <View key={i} style={styles.fundingItem}>
                <Text style={styles.fundingTitle}>{item.title}</Text>
                <Text style={styles.fundingDesc}>{item.desc}</Text>
              </View>
            ))}
          </View>

          {/* FAQ */}
          <View style={styles.section}>
            <Text style={styles.h2}>שאלות נפוצות</Text>
            {[
              { q: 'האם קורס יקר יותר = טוב יותר?', a: 'לא בהכרח. בדקו פרויקטים של בוגרים וחוות דעת אמיתיות.' },
              { q: 'מה המחיר הממוצע של קורס תכנות?', a: 'קורס ממוקד: 3,000–9,000 ₪. בוטקאמפ מלא: 14,000–22,000 ₪.' },
              { q: 'האם יש מימון לקורסים?', a: 'כן — קרן הסבה, לשכת תעסוקה, ISA ותשלומים ללא ריבית.' },
            ].map((item, i) => (
              <View key={i} style={styles.faqItem}>
                <Text style={styles.faqQ}>{item.q}</Text>
                <Text style={styles.faqA}>{item.a}</Text>
              </View>
            ))}
          </View>

          {/* CTA */}
          <View style={styles.ctaBlock}>
            <Text style={styles.ctaTitle}>רוצים למצוא קורס בתקציב שלכם?</Text>
            <Text style={styles.ctaBody}>ציינו תקציב ותחום — ה-AI של Kursi יחזיר התאמות רלוונטיות.</Text>
            <Pressable
              style={({ pressed }) => [styles.ctaBtn, pressed && { opacity: 0.85 }]}
              onPress={() => router.push('/chat')}
              accessibilityRole="button"
            >
              <Text style={styles.ctaBtnText}>חפשו קורס לפי תקציב</Text>
            </Pressable>
          </View>

          {/* Internal links */}
          <View style={styles.linksSection}>
            <Text style={styles.linksTitle}>מדריכים נוספים</Text>
            <View style={styles.linksGrid}>
              {[
                { label: 'קורס אונליין vs פרונטלי', route: '/compare/online-vs-frontal' },
                { label: 'בחירת קורס לפי עיר', route: '/guides/by-city' },
                { label: 'בחירת קורס לפי מטרה', route: '/guides/by-goal' },
                { label: 'תכנות למתחילים', route: '/guides/beginners-programming' },
                { label: 'קטלוג כל הקורסים', route: '/catalog' },
              ].map((link) => (
                <Pressable
                  key={link.route}
                  style={({ pressed }) => [styles.linkItem, pressed && { opacity: 0.75 }]}
                  onPress={() => router.push(link.route as any)}
                  accessibilityRole="link"
                >
                  <Text style={styles.linkText}>{link.label} →</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2EBDD' },
  content: { paddingBottom: 56 },
  innerContent: { maxWidth: 820, alignSelf: 'center' as any, width: '100%', paddingHorizontal: 16 },
  navBar: {
    flexDirection: 'row' as any, justifyContent: 'space-between' as any,
    alignItems: 'center' as any, paddingHorizontal: 16, paddingVertical: 12, gap: 10,
  },
  navBtn: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 0, backgroundColor: '#FAF5EA' },
  navBtnHome: { backgroundColor: '#FAF5EA' },
  navBtnPressed: { opacity: 0.7 },
  navBtnText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary },
  navBtnHomeText: { color: '#7A6F5F' },
  badge: {
    backgroundColor: 'transparent', borderWidth: 1,
    borderColor: '#1A1611', borderRadius: 0,
    paddingHorizontal: 12, paddingVertical: 5, marginBottom: 10,
    alignSelf: 'flex-end' as any, marginTop: 16,
  },
  badgeText: { fontSize: 12, fontFamily: 'Heebo-Bold', color: theme.colors.primary },
  h1: {
    fontSize: Platform.OS === 'web' ? 32 : 24, fontFamily: 'Heebo-Bold', color: '#1A1611',
    textAlign: 'right' as any, marginBottom: 12, lineHeight: Platform.OS === 'web' ? 44 : 34,
  },
  intro: {
    fontSize: 16, fontFamily: 'Heebo-Regular', color: '#7A6F5F',
    textAlign: 'right' as any, lineHeight: 26, marginBottom: 20,
  },
  insightCard: {
    backgroundColor: '#FFF7ED', borderRadius: 14, borderWidth: 1,
    borderColor: '#FED7AA', padding: 18, marginBottom: 20,
  },
  insightTitle: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#92400E', textAlign: 'right' as any, marginBottom: 6 },
  insightBody: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#78350F', textAlign: 'right' as any, lineHeight: 22 },
  tierCard: {
    borderRadius: 0, borderWidth: 2, overflow: 'hidden', marginBottom: 14,
  },
  tierHeader: { padding: 16 },
  tierLabel: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any },
  tierTitle: { fontSize: 14, fontFamily: 'Heebo-Medium', color: '#3A332A', textAlign: 'right' as any, marginTop: 2 },
  tierBody: { backgroundColor: '#FAF5EA', padding: 16 },
  tierDesc: {
    fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A',
    textAlign: 'right' as any, lineHeight: 22, marginBottom: 12,
  },
  suitableTag: {
    marginTop: 12, backgroundColor: '#FAF5EA', borderRadius: 0,
    paddingVertical: 8, paddingHorizontal: 12,
  },
  suitableText: { fontSize: 13, fontFamily: 'Heebo-Bold', color: '#3A332A', textAlign: 'right' as any },
  bulletList: { gap: 8 },
  bulletRow: { flexDirection: 'row-reverse' as any, gap: 8, alignItems: 'flex-start' as any },
  bulletDot: { fontSize: 14, color: theme.colors.primary, fontFamily: 'Heebo-Bold', lineHeight: 22 },
  bulletText: { flex: 1, fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  section: {
    backgroundColor: '#FAF5EA', borderRadius: 0, borderWidth: 1,
    borderColor: '#1A1611', padding: 20, marginBottom: 14,
  },
  h2: {
    fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611',
    textAlign: 'right' as any, marginBottom: 14, lineHeight: 28,
  },
  fundingItem: { marginBottom: 14 },
  fundingTitle: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 4 },
  fundingDesc: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  faqItem: { marginBottom: 14 },
  faqQ: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 6 },
  faqA: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  ctaBlock: { backgroundColor: '#FAF5EA', borderRadius: 0, padding: 24, alignItems: 'center' as any, marginTop: 8, marginBottom: 20 },
  ctaTitle: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'center' as any, marginBottom: 8 },
  ctaBody: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'center' as any, marginBottom: 16, lineHeight: 22 },
  ctaBtn: { backgroundColor: theme.colors.primary, paddingVertical: 12, paddingHorizontal: 28, borderRadius: 0 },
  ctaBtnText: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#FAF5EA' },
  linksSection: { marginBottom: 16 },
  linksTitle: { fontSize: 17, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 12 },
  linksGrid: { gap: 8 },
  linkItem: {
    paddingVertical: 12, paddingHorizontal: 16, backgroundColor: '#FAF5EA',
    borderRadius: 0, borderWidth: 1, borderColor: '#1A1611',
  },
  linkText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary, textAlign: 'right' as any },
});
